// src/pages/Users/Inscription/InscriptionPage.jsx

import React, { useState } from "react";
import { ToastContainer } from "react-toastify";

import useScrollToTop from "../../../hooks/useScollToTop";
import Reveal from "../../../components/Reveal/Reveal";

import AdherentsMiniTable from "./AdherentsMiniTable";
import { useInscriptionForm } from "./hooks/useInscriptionForm";

import InscriptionHero from "./components/InscriptionHero";
import SeasonTypePanel from "./components/SeasonTypePanel";
import GroupSelectionPanel from "./components/GroupSelectionPanel";
import IdentityPanel from "./components/IdentityPanel";
import ExtraInfoPanel from "./components/ExtraInfoPanel";
import ConsentPanel from "./components/ConsentPanel";
import FinalSummaryPanel from "./components/FinalSummaryPanel";
import GroupDetailsDrawer from "./components/GroupDetailsDrawer";

export default function InscriptionPage() {
  useScrollToTop();
  const form = useInscriptionForm();

  const [detailGroup, setDetailGroup] = useState(null);

  const detailDisabled = detailGroup
    ? !form.canRegisterToGroup(detailGroup)
    : false;

  const detailUnavailableReason = detailGroup
    ? form.getGroupUnavailableReason(detailGroup)
    : "";

  const handleSelectDetailGroup = () => {
    if (!detailGroup || detailDisabled) return;

    form.addGroup(detailGroup);
    setDetailGroup(null);
  };

  if (form.loading) {
    return (
      <main className="min-h-screen bg-slate-50 pt-[140px] dark:bg-slate-950">
        <div className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
          <div className="h-80 animate-pulse rounded-[2rem] bg-slate-200 dark:bg-slate-800" />
          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="h-40 animate-pulse rounded-[2rem] bg-slate-200 dark:bg-slate-800"
              />
            ))}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 pt-[120px] text-slate-900 dark:bg-slate-950 dark:text-white lg:pt-[150px]">
      <div className="absolute inset-x-0 top-0 h-[520px] bg-[radial-gradient(circle_at_top_left,rgba(1,62,142,0.20),transparent_34%),radial-gradient(circle_at_top_right,rgba(76,168,47,0.18),transparent_30%)]" />

      <div className="relative mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <Reveal>
          <InscriptionHero
            userInfo={form.userInfo}
            saisonActive={form.saisonActive}
          />
        </Reveal>

        <Reveal delay={80}>
          <AdherentsMiniTable className="mt-6" />
        </Reveal>

        <form onSubmit={form.handleSubmit} className="mt-8 space-y-6">
          <Reveal delay={100}>
            <SeasonTypePanel
              saisonActive={form.saisonActive}
              value={form.formData.statut_inscription}
              onChange={form.setInscriptionType}
            />
          </Reveal>

          <Reveal delay={140}>
            <GroupSelectionPanel
              disabled={!form.formData.statut_inscription}
              dojos={form.dojos}
              dojosById={form.dojosById}
              selectedDojo={form.selectedDojo}
              setSelectedDojo={form.setSelectedDojo}
              groups={form.displayedGroups}
              selectedGroups={form.selectedGroups}
              onAddGroup={form.addGroup}
              onRemoveGroup={form.removeGroup}
              groupError={form.groupError}
              isQuotaActive={form.isQuotaActive}
              canRegisterToGroup={form.canRegisterToGroup}
              getGroupUnavailableReason={form.getGroupUnavailableReason}
              onOpenGroupDetails={setDetailGroup}
            />
          </Reveal>

          <Reveal delay={180}>
            <IdentityPanel
              formData={form.formData}
              handleChange={form.handleChange}
              handleDateChange={form.handleDateChange}
              dateError={form.dateError}
              duplicateWarning={form.duplicateWarning}
              communes={form.communes}
              communeLoading={form.communeLoading}
            />
          </Reveal>

          <Reveal delay={220}>
            <ExtraInfoPanel
              formData={form.formData}
              handleChange={form.handleChange}
              setFormData={form.setFormData}
              belts={form.belts}
            />
          </Reveal>

          <Reveal delay={260}>
            <ConsentPanel
              formData={form.formData}
              handleChange={form.handleChange}
            />
          </Reveal>

          <Reveal delay={300}>
            <FinalSummaryPanel
              selectedGroups={form.selectedGroups}
              totalAmount={form.totalAmount}
              canSubmit={form.canSubmit}
              submitting={form.submitting}
            />
          </Reveal>
        </form>
      </div>
      <GroupDetailsDrawer
        group={detailGroup}
        dojo={detailGroup ? form.dojosById[String(detailGroup.dojo_id)] : null}
        disabled={detailDisabled}
        unavailableReason={detailUnavailableReason}
        onClose={() => setDetailGroup(null)}
        onSelect={handleSelectDetailGroup}
      />

      <ToastContainer />
    </main>
  );
}
